import React, { useState, useEffect } from 'react'
import './alloyRegistration.css'
import CustomSelect from '../../Core/common/CustomSelect'
import { useAuth } from '../../Core/Auth/AuthContext'
import { client } from '../../Core/client'
import { jwtDecode } from 'jwt-decode'
import {
  Steps,
  Button,
  Form,
  Input,
  Upload,
  Card,
  Row,
  Col,
  Divider,
  Typography,
  Avatar,
  message,
  Select,
  InputNumber
} from 'antd'
import {
  UserOutlined,
  InboxOutlined,
  CheckCircleOutlined,
  ArrowRightOutlined,
  ArrowLeftOutlined,
  PhoneOutlined,
  MailOutlined,
  IdcardOutlined,
  CarOutlined,
  SettingOutlined,
  SafetyOutlined,
  DollarOutlined
} from '@ant-design/icons'

const { Step } = Steps
const { Option } = Select
const { Title, Text } = Typography
const { Dragger } = Upload

const AlloyRegistration = () => {
  const { isAuthenticated, user: dealerInfo, getToken } = useAuth()
  const [currentStep, setCurrentStep] = useState(0)
  const [checkOtp, setCheckOtp] = useState(false)
  const [loading, setLoading] = useState(false)
  const [id, setId] = useState('')
  // State for all dropdown data
  const [alloys, setAlloys] = useState([])
  const [inches, setInches] = useState([])
  const [pcds, setPcds] = useState([])
  const [holes, setHoles] = useState([])
  const [finishes, setFinishes] = useState([])

  // State for selected values
  const [alloyModal, setAlloyModal] = useState('')
  const [alloyInches, setAlloyInches] = useState('')
  const [alloyPcd, setAlloyPcd] = useState('')
  const [alloyHoles, setAlloyHoles] = useState('')
  const [alloyFinish, setAlloyFinish] = useState('')

  // Upload states
  const [uploadingWarrantyCard, setUploadingWarrantyCard] = useState(false)
  const [uploadingVehicleImage, setUploadingVehicleImage] = useState(false)

  // Main warranty object
  const [alloyWarranty, setAlloyWarranty] = useState({
    customer_name: '',
    mobile_no: '',
    warranty_card_no: '',
    dop: '',
    vehicle_no: '',
    vehicle_model: '',
    no_of_alloys: 4,
    amount: '',
    otp: '',
    warranty_card_image: null,
    vehicle_image: null,
    id: null
  })

  const steps = [
    {
      title: 'Customer Info',
      icon: <UserOutlined />,
      description: 'Customer details'
    },
    {
      title: 'Warranty Card',
      icon: <IdcardOutlined />,
      description: 'Card information'
    },
    {
      title: 'Alloy Details',
      icon: <SettingOutlined />,
      description: 'Wheel specifications'
    },
    {
      title: 'Vehicle Info',
      icon: <CarOutlined />,
      description: 'Vehicle details'
    },
    {
      title: 'Review & Submit',
      icon: <CheckCircleOutlined />,
      description: 'Final review'
    }
  ]

  // Load all required data on component mount
  useEffect(() => {
    Promise.all([
      client.get('/alloys'),
      client.get('/alloys/inches'),
      client.get('/alloys/pcds'),
      client.get('/alloys/holes'),
      client.get('/alloys/finishes')
    ])
      .then(([alloyRes, inchRes, pcdRes, holesRes, finishRes]) => {
        // Add null checks and ensure arrays
        setAlloys(Array.isArray(alloyRes.data) ? alloyRes.data : [])
        setInches(Array.isArray(inchRes.data) ? inchRes.data : [])
        setPcds(Array.isArray(pcdRes.data) ? pcdRes.data : [])
        setHoles(Array.isArray(holesRes.data) ? holesRes.data : [])
        setFinishes(Array.isArray(finishRes.data) ? finishRes.data : [])
      })
      .catch(err => {
        console.error('Failed to load dropdown data:', err)
        message.error('Failed to load form data. Please refresh the page.')

        // Set empty arrays as fallback
        setAlloys([])
        setInches([])
        setPcds([])
        setHoles([])
        setFinishes([])
      })
  }, [])

  const nextStep = () => {
    if (validateCurrentStep()) {
      setCurrentStep(current => current + 1)
    }
  }

  const prevStep = () => {
    setCurrentStep(current => current - 1)
  }

  const validateCurrentStep = () => {
    switch (currentStep) {
      case 0:
        if (!alloyWarranty.customer_name || !alloyWarranty.mobile_no) {
          message.error('Please fill in all customer information')
          return false
        }
        break
      case 1:
        if (!alloyWarranty.warranty_card_no || !alloyWarranty.dop) {
          message.error(
            'Please enter warranty card number and date of purchase'
          )
          return false
        }
        if (!alloyWarranty.warranty_card_image) {
          message.error('Please upload warranty card image')
          return false
        }
        break
      case 2:
        if (
          !alloyModal ||
          !alloyInches ||
          !alloyPcd ||
          !alloyHoles ||
          !alloyFinish
        ) {
          message.error('Please select all alloy specifications')
          return false
        }
        break
      case 3:
        if (!alloyWarranty.vehicle_no || !alloyWarranty.vehicle_model) {
          message.error('Please fill in all vehicle information')
          return false
        }
        if (!alloyWarranty.vehicle_image) {
          message.error('Please upload vehicle image')
          return false
        }
        break
    }
    return true
  }

  const handleSubmit = async () => {
    if (!validateCurrentStep()) return

    try {
      setLoading(true)
      const token = getToken()
      const decodedToken = jwtDecode(token)

      const warrantyData = {
        ...alloyWarranty,
        alloy_model: parseInt(alloyModal),
        inches_id: parseInt(alloyInches),
        pcd_id: parseInt(alloyPcd),
        holes_id: parseInt(alloyHoles),
        finish_id: parseInt(alloyFinish),
        dealer_id: decodedToken.dealer_id,
        mobile_no: parseInt(alloyWarranty.mobile_no),
        amount: alloyWarranty.amount ? parseInt(alloyWarranty.amount) : null
      }

      const response = await client.post('/warranty/alloy', warrantyData)

      if (response.data?.success) {
        message.success('Warranty registered successfully!')
        // Store the warranty ID for OTP operations
        setAlloyWarranty(prev => ({
          ...prev,
          id: response.data.warranty?.id
        }))
        setCheckOtp(true)
      } else {
        message.error(response.data?.message || 'Registration failed')
      }
    } catch (error) {
      console.error('Registration error:', error)
      message.error(error.response?.data?.message || 'Registration failed')
    } finally {
      setLoading(false)
    }
  }

  const generateOtp = () => {
    // Check if we have a warranty ID
    if (!alloyWarranty.id) {
      message.error('No warranty registration found. Please register first.')
      return
    }

    // generate 6 digit otp
    const otp = Math.floor(100000 + Math.random() * 900000).toString()
    console.log('Generated OTP:', otp)

    // For Ant Design v5 Input.OTP, we can set the string directly
    setAlloyWarranty(prev => ({
      ...prev,
      otp
    }))

    client
      .post(`/alloys/update-otp`, {
        otp,
        id: alloyWarranty.id
      })
      .then(res => {
        console.log('OTP updated successfully:', res)

        // Send OTP via backend SMS API
        client
          .post('/sms/send-otp', {
            mobile_no: alloyWarranty.mobile_no,
            otp: otp
          })
          .then(res => {
            console.log('OTP SMS sent successfully:', res)
            message.success('OTP sent to your mobile number')
          })
          .catch(err => {
            console.log('OTP SMS sending error:', err)
            message.warning(
              'OTP generated but SMS sending failed. Please try again.'
            )
          })
      })
      .catch(err => {
        console.log('OTP update error:', err)
        message.error('Failed to generate OTP. Please try again.')
      })
  }

  const verifyOtp = () => {
    if (!alloyWarranty.userOtp) {
      message.error('Please enter the OTP')
      return
    }

    // Check if we have a warranty ID
    if (!alloyWarranty.id) {
      message.error('No warranty registration found. Please register first.')
      return
    }

    // Get OTP value - In Ant Design v5, the value might be an array or string
    let otpValue = alloyWarranty.userOtp
    if (Array.isArray(otpValue)) {
      otpValue = otpValue.join('')
    } else {
      otpValue = String(otpValue)
    }

    console.log('Verifying OTP:', otpValue)

    // Check if the OTP is complete
    if (otpValue.length !== 6) {
      message.error('Please enter the complete 6-digit OTP')
      return
    }

    client
      .post(`/alloys/verify-otp`, {
        id: alloyWarranty.id,
        otp: otpValue
      })
      .then(res => {
        console.log('OTP verification successful:', res)

        // Send confirmation SMS via backend API
        client
          .post('/sms/send-confirmation', {
            mobile_no: alloyWarranty.mobile_no
          })
          .then(res => {
            console.log('SMS confirmation sent:', res)
          })
          .catch(err => {
            console.log('SMS confirmation error:', err)
          })

        message.success('OTP Verified Successfully')

        // Show success message for 3 seconds before reloading
        setTimeout(() => {
          message.success('Registration completed successfully!')
          window.location.reload()
        }, 3000)
      })
      .catch(err => {
        console.error('OTP verification error:', err)
        message.error('OTP Verification Failed. Please try again.')
      })
  }

  // Handle warranty card image upload
  const handleWarrantyCardUpload = async file => {
    setUploadingWarrantyCard(true)

    try {
      console.log('Uploading file:', file)
      console.log('File details:', {
        name: file.name,
        size: file.size,
        type: file.type
      })

      const formData = new FormData()
      // Use 'file' as field name for the new endpoint
      formData.append('file', file)
      // Add bucket and folder parameters
      formData.append('bucket', 'warranty-images')
      formData.append('folder', 'warranty-cards')

      console.log('Making request to:', '/upload/storj-v2')

      const response = await client.post('/upload/storj-v2', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      console.log('Upload response:', response)
      console.log('Upload response data:', response.data)

      // New endpoint returns structured response with success flag
      if (
        response.data &&
        response.data.success &&
        response.data.data &&
        response.data.data.length > 0
      ) {
        const uploadedFile = response.data.data[0]
        console.log('Uploaded file object:', uploadedFile)

        // Store the image URL (proxy URL is reliable, no need for complex metadata)
        setAlloyWarranty(prev => ({
          ...prev,
          warranty_card_image: uploadedFile.fileUrl
        }))
        message.success('Warranty card image uploaded successfully!')
        return false // Prevent default upload behavior
      } else {
        console.error('Unexpected response format:', response.data)
        throw new Error(
          response.data?.message || 'Upload failed - unexpected response format'
        )
      }
    } catch (error) {
      console.error('Warranty card upload error:', error)
      console.error('Error response:', error.response?.data)
      console.error('Error status:', error.response?.status)
      console.error('Error message:', error.message)

      let errorMessage = 'Failed to upload warranty card image'

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid file format or missing file'
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error during upload'
      } else if (error.message) {
        errorMessage = error.message
      }

      message.error(errorMessage)
    } finally {
      setUploadingWarrantyCard(false)
    }

    return false // Prevent default upload behavior
  }

  // Handle vehicle image upload
  const handleVehicleImageUpload = async file => {
    setUploadingVehicleImage(true)

    try {
      // Validate file size (5MB limit)
      const maxSize = 5 * 1024 * 1024 // 5MB in bytes
      if (file.size > maxSize) {
        message.error('File size must be less than 5MB')
        return false
      }

      // Validate file type
      const allowedTypes = [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/webp'
      ]
      if (!allowedTypes.includes(file.type)) {
        message.error('Please upload only JPG, PNG, or WebP images')
        return false
      }

      console.log('Uploading vehicle image:', file)
      console.log('File details:', {
        name: file.name,
        size: file.size,
        type: file.type
      })

      const formData = new FormData()
      formData.append('file', file)
      formData.append('bucket', 'warranty-images')
      formData.append('folder', 'vehicle-images')

      console.log('Making request to:', '/upload/storj-v2')

      // Show upload progress
      message.loading('Uploading vehicle image...', 0)

      const response = await client.post('/upload/storj-v2', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: progressEvent => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          )
          console.log(`Upload progress: ${percentCompleted}%`)
        }
      })

      // Hide loading message
      message.destroy()

      console.log('Vehicle image upload response:', response)

      if (
        response.data &&
        response.data.success &&
        response.data.data &&
        response.data.data.length > 0
      ) {
        const uploadedFile = response.data.data[0]
        console.log('Uploaded vehicle image:', uploadedFile)

        setAlloyWarranty(prev => ({
          ...prev,
          vehicle_image: uploadedFile.fileUrl
        }))
        message.success('Vehicle image uploaded successfully!')
        return false
      } else {
        console.error('Unexpected response format:', response.data)
        throw new Error(
          response.data?.message || 'Upload failed - unexpected response format'
        )
      }
    } catch (error) {
      console.error('Vehicle image upload error:', error)

      // Hide loading message on error
      message.destroy()

      let errorMessage = 'Failed to upload vehicle image'
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.response?.status === 400) {
        errorMessage = 'Invalid file format or missing file'
      } else if (error.response?.status === 413) {
        errorMessage = 'File size too large. Please upload a smaller image.'
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error during upload. Please try again.'
      } else if (error.message) {
        errorMessage = error.message
      }

      message.error(errorMessage)
    } finally {
      setUploadingVehicleImage(false)
    }

    return false
  }

  // Get the current image URL - now handles both absolute and relative URLs
  const getImageUrl = (imageType = 'warranty') => {
    const imageUrl =
      imageType === 'vehicle'
        ? alloyWarranty.vehicle_image
        : alloyWarranty.warranty_card_image

    console.log(`=== ${imageType} Image URL Debug ===`)
    console.log('Raw image URL from state:', imageUrl)

    if (!imageUrl) {
      console.log('No image URL found')
      return null
    }

    // If it's already a full URL (production), return as is
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      console.log('Using absolute URL:', imageUrl)
      return imageUrl
    }

    // If it's a relative URL (development fallback), construct full URL using client base URL
    const baseURL = client.defaults.baseURL
    const fullUrl = `${baseURL}${imageUrl}`
    console.log('Constructed full URL from relative:', fullUrl)
    console.log('Base URL:', baseURL)
    console.log('Relative URL:', imageUrl)
    console.log(`=== End ${imageType} Image URL Debug ===`)
    return fullUrl
  }

  // Remove warranty card image
  const removeWarrantyCardImage = () => {
    setAlloyWarranty(prev => ({
      ...prev,
      warranty_card_image: null
    }))
    message.success('Warranty card image removed')
  }

  // Remove vehicle image
  const removeVehicleImage = () => {
    setAlloyWarranty(prev => ({
      ...prev,
      vehicle_image: null
    }))
    message.success('Vehicle image removed')
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className='step-content'>
            <div className='step-header'>
              <UserOutlined className='step-icon' />
              <Title level={3} className='step-title'>
                Customer Information
              </Title>
              <Text className='step-description'>
                Enter customer details for warranty registration
              </Text>
            </div>

            <div className='form-group'>
              <label className='form-label'>
                <Text strong>Customer Name</Text>
                <Text type='danger'> *</Text>
              </label>
              <Input
                size='large'
                placeholder='Enter full name'
                value={alloyWarranty.customer_name}
                onChange={e =>
                  setAlloyWarranty(prev => ({
                    ...prev,
                    customer_name: e.target.value
                  }))
                }
                className='form-input'
              />
            </div>

            <div className='form-group'>
              <label className='form-label'>
                <Text strong>Mobile Number</Text>
                <Text type='danger'> *</Text>
              </label>
              <Input
                size='large'
                placeholder='Enter 10-digit mobile number'
                value={alloyWarranty.mobile_no}
                onChange={e =>
                  setAlloyWarranty(prev => ({
                    ...prev,
                    mobile_no: e.target.value
                  }))
                }
                maxLength={10}
                className='form-input'
              />
            </div>
          </div>
        )

      case 1:
        return (
          <div className='step-content'>
            <div className='step-header'>
              <IdcardOutlined className='step-icon' />
              <Title level={3} className='step-title'>
                Warranty Card Details
              </Title>
              <Text className='step-description'>
                Provide warranty card information and upload images
              </Text>
            </div>

            <div className='form-group'>
              <label className='form-label'>
                <Text strong>Warranty Card Number</Text>
                <Text type='danger'> *</Text>
              </label>
              <Input
                size='large'
                placeholder='Enter warranty card number'
                value={alloyWarranty.warranty_card_no}
                onChange={e =>
                  setAlloyWarranty(prev => ({
                    ...prev,
                    warranty_card_no: e.target.value
                  }))
                }
                className='form-input'
              />
            </div>

            <div className='form-group'>
              <label className='form-label'>
                <Text strong>Date of Purchase</Text>
                <Text type='danger'> *</Text>
              </label>
              <Input
                size='large'
                type='date'
                placeholder='Select purchase date'
                value={alloyWarranty.dop}
                onChange={e =>
                  setAlloyWarranty(prev => ({
                    ...prev,
                    dop: e.target.value
                  }))
                }
                className='form-input'
              />
            </div>

            <div className='form-group'>
              <label className='form-label'>
                <Text strong>Amount (Optional)</Text>
              </label>
              <InputNumber
                size='large'
                placeholder='Enter amount'
                value={alloyWarranty.amount}
                onChange={value =>
                  setAlloyWarranty(prev => ({ ...prev, amount: value }))
                }
                className='form-input'
                style={{ width: '100%' }}
                min={0}
                formatter={value =>
                  `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                }
                parser={value => value.replace(/₹\s?|(,*)/g, '')}
              />
            </div>

            <div className='form-group'>
              <label className='form-label'>
                <Text strong>Warranty Card Image</Text>
                <Text type='danger'> *</Text>
              </label>
              <Text
                type='secondary'
                style={{ display: 'block', marginBottom: '8px' }}
              >
                Upload a clear photo of your warranty card
              </Text>

              {!alloyWarranty.warranty_card_image ? (
                <Dragger
                  name='warranty_card'
                  multiple={false}
                  accept='image/*'
                  beforeUpload={handleWarrantyCardUpload}
                  showUploadList={false}
                  className='upload-dragger'
                >
                  <p className='ant-upload-drag-icon'>
                    <InboxOutlined />
                  </p>
                  <p className='ant-upload-text'>
                    {uploadingWarrantyCard
                      ? 'Uploading...'
                      : 'Click or drag warranty card image to upload'}
                  </p>
                  <p className='ant-upload-hint'>
                    Support for JPG, PNG, JPEG files. Max size 5MB.
                  </p>
                </Dragger>
              ) : (
                <div className='uploaded-image-preview'>
                  <img
                    src={getImageUrl()}
                    alt='Warranty Card'
                    style={{
                      width: '100%',
                      maxWidth: '300px',
                      height: 'auto',
                      borderRadius: '8px',
                      border: '1px solid #d9d9d9'
                    }}
                    onError={e => {
                      console.error('Image failed to load:', e.target.src)
                      console.error('Error event:', e)
                    }}
                    onLoad={() => {
                      console.log('Image loaded successfully:', getImageUrl())
                    }}
                  />
                  <div style={{ marginTop: '8px' }}>
                    <Button
                      type='primary'
                      size='small'
                      onClick={removeWarrantyCardImage}
                      danger
                    >
                      Remove Image
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )

      case 2:
        return (
          <div className='step-content'>
            <div className='step-header'>
              <SettingOutlined className='step-icon' />
              <Title level={3} className='step-title'>
                Alloy Wheel Specifications
              </Title>
              <Text className='step-description'>
                Select your alloy wheel details
              </Text>
            </div>

            <div className='form-group'>
              <label className='form-label'>
                <Text strong>Alloy Model</Text>
                <Text type='danger'> *</Text>
              </label>
              <CustomSelect
                size='large'
                list={alloys}
                placeholder='Select alloy model'
                name='modalName'
                value={alloyModal}
                onChange={setAlloyModal}
                className='form-select'
              />
            </div>

            <Row gutter={[8, 0]}>
              <Col xs={24} sm={12}>
                <div className='form-group'>
                  <label className='form-label'>
                    <Text strong>Size (Inches)</Text>
                    <Text type='danger'> *</Text>
                  </label>
                  <CustomSelect
                    size='large'
                    list={inches}
                    placeholder='Select size'
                    name='inches'
                    value={alloyInches}
                    onChange={setAlloyInches}
                    className='form-select'
                  />
                </div>
              </Col>
              <Col xs={24} sm={12}>
                <div className='form-group'>
                  <label className='form-label'>
                    <Text strong>PCD</Text>
                    <Text type='danger'> *</Text>
                  </label>
                  <CustomSelect
                    size='large'
                    list={pcds}
                    placeholder='Select PCD'
                    name='pcd'
                    value={alloyPcd}
                    onChange={setAlloyPcd}
                    className='form-select'
                  />
                </div>
              </Col>
            </Row>

            <Row gutter={[8, 0]}>
              <Col xs={24} sm={12}>
                <div className='form-group'>
                  <label className='form-label'>
                    <Text strong>Number of Holes</Text>
                    <Text type='danger'> *</Text>
                  </label>
                  <CustomSelect
                    size='large'
                    list={holes}
                    placeholder='Select holes'
                    name='holes'
                    value={alloyHoles}
                    onChange={setAlloyHoles}
                    className='form-select'
                  />
                </div>
              </Col>
              <Col xs={24} sm={12}>
                <div className='form-group'>
                  <label className='form-label'>
                    <Text strong>Finish</Text>
                    <Text type='danger'> *</Text>
                  </label>
                  <CustomSelect
                    size='large'
                    list={finishes}
                    placeholder='Select finish'
                    name='finish'
                    value={alloyFinish}
                    onChange={setAlloyFinish}
                    className='form-select'
                  />
                </div>
              </Col>
            </Row>

            <div className='form-group'>
              <label className='form-label'>
                <Text strong>Number of Alloys</Text>
              </label>
              <InputNumber
                size='large'
                value={alloyWarranty.no_of_alloys}
                onChange={value =>
                  setAlloyWarranty(prev => ({ ...prev, no_of_alloys: value }))
                }
                min={1}
                max={10}
                className='form-input'
                style={{ width: '100%' }}
              />
            </div>
          </div>
        )

      case 3:
        return (
          <div className='step-content'>
            <div className='step-header'>
              <CarOutlined className='step-icon' />
              <Title level={3} className='step-title'>
                Vehicle Information
              </Title>
              <Text className='step-description'>
                Enter your vehicle details and upload vehicle image
              </Text>
            </div>

            <div className='form-group'>
              <label className='form-label'>
                <Text strong>Vehicle Number</Text>
                <Text type='danger'> *</Text>
              </label>
              <Input
                size='large'
                placeholder='e.g., KA01AB1234'
                value={alloyWarranty.vehicle_no}
                onChange={e =>
                  setAlloyWarranty(prev => ({
                    ...prev,
                    vehicle_no: e.target.value.toUpperCase()
                  }))
                }
                className='form-input'
              />
            </div>

            <div className='form-group'>
              <label className='form-label'>
                <Text strong>Vehicle Model</Text>
                <Text type='danger'> *</Text>
              </label>
              <Input
                size='large'
                placeholder='e.g., Mahindra Thar, Hyundai Creta'
                value={alloyWarranty.vehicle_model}
                onChange={e =>
                  setAlloyWarranty(prev => ({
                    ...prev,
                    vehicle_model: e.target.value
                  }))
                }
                className='form-input'
              />
            </div>

            <div className='form-group'>
              <label className='form-label'>
                <Text strong>Vehicle Image</Text>
                <Text type='danger'> *</Text>
              </label>
              <Text
                type='secondary'
                style={{ display: 'block', marginBottom: '12px' }}
              >
                📷 Upload a clear photo of your vehicle with alloy wheels and
                Number Plate visible
              </Text>

              {!alloyWarranty.vehicle_image ? (
                <Dragger
                  name='vehicle_image'
                  multiple={false}
                  accept='image/jpeg,image/jpg,image/png,image/webp'
                  beforeUpload={handleVehicleImageUpload}
                  showUploadList={false}
                  className='upload-dragger'
                  disabled={uploadingVehicleImage}
                  style={{
                    backgroundColor: uploadingVehicleImage
                      ? '#f5f5f5'
                      : '#fafafa',
                    borderColor: uploadingVehicleImage ? '#d9d9d9' : '#d9d9d9'
                  }}
                >
                  <p className='ant-upload-drag-icon'>
                    <InboxOutlined
                      style={{
                        color: uploadingVehicleImage ? '#bfbfbf' : '#1890ff'
                      }}
                    />
                  </p>
                  <p className='ant-upload-text'>
                    {uploadingVehicleImage
                      ? '📤 Uploading vehicle image...'
                      : '📷 Click or drag vehicle image to upload'}
                  </p>
                  <p className='ant-upload-hint'>
                    ✅ Supports JPG, PNG, WebP • Max 5MB • High quality
                    preferred
                  </p>
                  {uploadingVehicleImage && (
                    <div style={{ marginTop: '8px' }}>
                      <Text type='secondary' style={{ fontSize: '12px' }}>
                        Please wait while your image is being uploaded...
                      </Text>
                    </div>
                  )}
                </Dragger>
              ) : (
                <div className='uploaded-image-preview'>
                  <div
                    style={{
                      position: 'relative',
                      display: 'inline-block',
                      width: '100%',
                      maxWidth: '350px'
                    }}
                  >
                    <img
                      src={getImageUrl('vehicle')}
                      alt='Vehicle with Alloy Wheels'
                      style={{
                        width: '100%',
                        height: 'auto',
                        borderRadius: '12px',
                        border: '2px solid #52c41a',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        transition: 'all 0.3s ease'
                      }}
                      onError={e => {
                        console.error(
                          'Vehicle image failed to load:',
                          e.target.src
                        )
                        console.error('Error event:', e)
                        message.error(
                          'Failed to load vehicle image. Please try uploading again.'
                        )
                      }}
                      onLoad={() => {
                        console.log(
                          'Vehicle image loaded successfully:',
                          getImageUrl('vehicle')
                        )
                      }}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        backgroundColor: '#52c41a',
                        color: 'white',
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px'
                      }}
                    >
                      ✓
                    </div>
                  </div>
                  <div
                    style={{
                      marginTop: '12px',
                      display: 'flex',
                      gap: '8px',
                      alignItems: 'center'
                    }}
                  >
                    <Button
                      type='primary'
                      size='small'
                      onClick={removeVehicleImage}
                      danger
                      icon='🗑️'
                    >
                      Remove Image
                    </Button>
                    <Text type='secondary' style={{ fontSize: '12px' }}>
                      ✅ Vehicle image uploaded successfully
                    </Text>
                  </div>
                </div>
              )}
            </div>
          </div>
        )

      case 4:
        return (
          <div className='step-content'>
            <div className='step-header'>
              <CheckCircleOutlined className='step-icon' />
              <Title level={3} className='step-title'>
                Review & Submit
              </Title>
              <Text className='step-description'>
                Please review your information before submitting
              </Text>
            </div>

            <div className='review-section'>
              <div className='review-card'>
                <Title level={5}>Customer Information</Title>
                <div className='review-item'>
                  <Text type='secondary'>Name:</Text>
                  <Text strong>{alloyWarranty.customer_name}</Text>
                </div>
                <div className='review-item'>
                  <Text type='secondary'>Mobile:</Text>
                  <Text strong>{alloyWarranty.mobile_no}</Text>
                </div>
              </div>

              <div className='review-card'>
                <Title level={5}>Warranty Details</Title>
                <div className='review-item'>
                  <Text type='secondary'>Card Number:</Text>
                  <Text strong>{alloyWarranty.warranty_card_no}</Text>
                </div>
                <div className='review-item'>
                  <Text type='secondary'>Date of Purchase:</Text>
                  <Text strong>{alloyWarranty.dop}</Text>
                </div>
                {alloyWarranty.amount && (
                  <div className='review-item'>
                    <Text type='secondary'>Amount:</Text>
                    <Text strong>₹{alloyWarranty.amount}</Text>
                  </div>
                )}
                {alloyWarranty.warranty_card_image && (
                  <div className='review-item'>
                    <Text type='secondary'>Warranty Card Image:</Text>
                    <div style={{ marginTop: '8px' }}>
                      <img
                        src={getImageUrl()}
                        alt='Warranty Card'
                        style={{
                          width: '100%',
                          maxWidth: '200px',
                          height: 'auto',
                          borderRadius: '4px',
                          border: '1px solid #d9d9d9'
                        }}
                        onError={e => {
                          console.error('Image failed to load:', e.target.src)
                          console.error('Error event:', e)
                        }}
                        onLoad={() => {
                          console.log(
                            'Review image loaded successfully:',
                            getImageUrl()
                          )
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className='review-card'>
                <Title level={5}>Alloy Specifications</Title>
                <div className='review-item'>
                  <Text type='secondary'>Model:</Text>
                  <Text strong>
                    {alloys.find(a => a.id == alloyModal)?.modalName ||
                      alloys.find(a => a.id == alloyModal)?.name ||
                      alloyModal}
                  </Text>
                </div>
                <div className='review-item'>
                  <Text type='secondary'>Size:</Text>
                  <Text strong>
                    {inches.find(i => i.id == alloyInches)?.inches ||
                      inches.find(i => i.id == alloyInches)?.name ||
                      alloyInches}
                    "
                  </Text>
                </div>
                <div className='review-item'>
                  <Text type='secondary'>PCD:</Text>
                  <Text strong>
                    {pcds.find(p => p.id == alloyPcd)?.pcd ||
                      pcds.find(p => p.id == alloyPcd)?.name ||
                      alloyPcd}
                  </Text>
                </div>
                <div className='review-item'>
                  <Text type='secondary'>Holes:</Text>
                  <Text strong>
                    {holes.find(h => h.id == alloyHoles)?.holes ||
                      holes.find(h => h.id == alloyHoles)?.name ||
                      alloyHoles}
                  </Text>
                </div>
                <div className='review-item'>
                  <Text type='secondary'>Finish:</Text>
                  <Text strong>
                    {finishes.find(f => f.id == alloyFinish)?.finish ||
                      finishes.find(f => f.id == alloyFinish)?.name ||
                      alloyFinish}
                  </Text>
                </div>
                <div className='review-item'>
                  <Text type='secondary'>Quantity:</Text>
                  <Text strong>{alloyWarranty.no_of_alloys}</Text>
                </div>
              </div>

              <div className='review-card'>
                <Title level={5}>Vehicle Information</Title>
                <div className='review-item'>
                  <Text type='secondary'>Number:</Text>
                  <Text strong>{alloyWarranty.vehicle_no}</Text>
                </div>
                <div className='review-item'>
                  <Text type='secondary'>Model:</Text>
                  <Text strong>{alloyWarranty.vehicle_model}</Text>
                </div>
                {alloyWarranty.vehicle_image && (
                  <div className='review-item'>
                    <Text type='secondary'>Vehicle Image:</Text>
                    <div style={{ marginTop: '8px' }}>
                      <img
                        src={getImageUrl('vehicle')}
                        alt='Vehicle'
                        style={{
                          width: '100%',
                          maxWidth: '200px',
                          height: 'auto',
                          borderRadius: '4px',
                          border: '1px solid #d9d9d9'
                        }}
                        onError={e => {
                          console.error(
                            'Vehicle image failed to load:',
                            e.target.src
                          )
                          console.error('Error event:', e)
                        }}
                        onLoad={() => {
                          console.log(
                            'Review vehicle image loaded successfully:',
                            getImageUrl('vehicle')
                          )
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  const renderOtpVerification = () => (
    <div className='otp-section'>
      <div className='success-header'>
        <div className='success-icon'>
          <CheckCircleOutlined />
        </div>
        <Title level={3}>Registration Complete!</Title>
        <Text className='success-description'>
          Please verify with OTP to activate your warranty
        </Text>
      </div>

      {alloyWarranty.otp === '' ? (
        <div className='otp-actions'>
          <Button
            type='primary'
            size='large'
            onClick={generateOtp}
            loading={loading}
            className='full-width-btn'
          >
            Send OTP to {alloyWarranty.mobile_no}
          </Button>
        </div>
      ) : (
        <div className='otp-verification'>
          <div className='form-group'>
            <label className='form-label'>
              <Text strong>Enter OTP</Text>
            </label>
            <Input
              size='large'
              placeholder='Enter 6-digit OTP'
              value={alloyWarranty.userOtp}
              onChange={e =>
                setAlloyWarranty(prev => ({ ...prev, userOtp: e.target.value }))
              }
              maxLength={6}
              className='form-input'
            />
          </div>

          <div className='otp-actions'>
            <Button
              type='primary'
              size='large'
              onClick={verifyOtp}
              loading={loading}
              className='full-width-btn'
            >
              Verify & Activate Warranty
            </Button>

            <Button
              type='link'
              onClick={generateOtp}
              loading={loading}
              className='resend-btn'
            >
              Resend OTP
            </Button>
          </div>
        </div>
      )}
    </div>
  )

  return (
    <div className='warranty-registration'>
      {/* Header with Plati Logo */}
      <header className='app-header'>
        <div className='header-content'>
          <div className='logo-section'>
            <img
              src='/images/plati-logo.png'
              alt='PLATI India'
              className='w-24 h-10'
            />
          </div>
          {isAuthenticated && dealerInfo && (
            <div className='dealer-info'>
              <Avatar icon={<UserOutlined />} />
              <div className='dealer-details'>
                <Text strong className='dealer-name'>
                  {dealerInfo.dealerName}
                </Text>
                <Text className='dealer-contact'>{dealerInfo.mobile}</Text>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className='hero-section'>
        <div className='hero-content'>
          <div className='hero-icon'>
            <SafetyOutlined />
          </div>
          <Title level={2} className='hero-title'>
            Alloy Wheel Warranty Registration
          </Title>
          <Text className='hero-subtitle'>
            Secure your investment with our comprehensive warranty coverage
          </Text>
          <div className='hero-features'>
            <div className='feature-item'>
              <CheckCircleOutlined className='feature-icon' />
              <Text>1 Year Warranty</Text>
            </div>
            <div className='feature-item'>
              <CheckCircleOutlined className='feature-icon' />
              <Text>Quality Assured</Text>
            </div>
            <div className='feature-item'>
              <CheckCircleOutlined className='feature-icon' />
              <Text>Easy Claims</Text>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className='main-content'>
        <div className='container'>
          {!checkOtp ? (
            <>
              {/* Form Content */}
              <div className='form-container'>
                <Card className='form-card'>{renderStepContent()}</Card>

                {/* Navigation Buttons */}
                <div className='form-actions'>
                  {currentStep > 0 && (
                    <Button
                      size='large'
                      onClick={prevStep}
                      className='back-btn'
                    >
                      <ArrowLeftOutlined /> Back
                    </Button>
                  )}

                  {currentStep < steps.length - 1 ? (
                    <Button
                      type='primary'
                      size='large'
                      onClick={nextStep}
                      className='next-btn'
                    >
                      Continue <ArrowRightOutlined />
                    </Button>
                  ) : (
                    <Button
                      type='primary'
                      size='large'
                      onClick={handleSubmit}
                      loading={loading}
                      className='submit-btn'
                    >
                      Submit Registration
                    </Button>
                  )}
                </div>
              </div>
            </>
          ) : (
            <Card className='otp-card'>{renderOtpVerification()}</Card>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className='app-footer'>
        <div className='footer-content'>
          <div className='footer-section'>
            <div className='flex items-center w-full justify-center'>
              <img
                src='/images/plati-logo.png'
                alt='PLATI India'
                className='w-24 h-10'
              />
            </div>
            <Text className='footer-text'>
              Best Alloy Wheels Brand in India
            </Text>
          </div>
          <div className='footer-section'>
            <div className='contact-item'>
              <PhoneOutlined className='contact-icon' />
              <Text>+91-99157-04380</Text>
            </div>
            <div className='contact-item'>
              <MailOutlined className='contact-icon' />
              <Text>sales@platiindia.com</Text>
            </div>
          </div>
        </div>
        <Divider className='footer-divider' />
        <div className='footer-bottom'>
          <Text className='copyright'>
            © 2025 Plati India. All Rights Reserved.
          </Text>
        </div>
      </footer>
    </div>
  )
}

export default AlloyRegistration
